import { Dialog, Menu } from '@headlessui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { getAutoReplyMessageAssets } from 'api/autoReply';
import { listLineFriends } from 'api/line';
import { changeScenarioState, getScenario, manualEnrollScenario, publishScenario, testSendScenario, updateScenario } from 'api/scenarios';
import type { ScenarioAssetReference, ScenarioConditionMatchV1, ScenarioConditionV1, ScenarioDefinitionV1, ScenarioJsonValue, ScenarioManualCommitResponse, ScenarioManualFriendsCommitResponse, ScenarioManualPreviewResponse, ScenarioManualSkipReason, ScenarioMessageV1, ScenarioPublishExpectedImpact, ScenarioPublishImpact, ScenarioRecord, ScenarioState, ScenarioStepV1, ScenarioTestSendCommitResponse, ScenarioTestSendNotSentReason, ScenarioTestSendOutcome, ScenarioTestSendPrepareResponse } from 'api/scenarios';
import type { TAutoReplyMessageAsset } from 'api/autoReply';
import { Alert } from 'components/Alert';
import { ConfirmModal } from 'components/ConfirmModal';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import { Pill } from 'components/ManagementCard';
import { Modal } from 'components/Modal';
import { useOrganizationScopeOrNull, useUnsavedInputGuard } from 'components/OrganizationScope/context';
import { Snackbar } from 'components/Snackbar';
import { TagSelector } from 'components/TagSelector';
import { Textarea } from 'components/Textarea';
import { pageCardClasses } from 'components/AppLayout/styles';
import { queryKeys } from 'constants/queryKeys';
import { routes } from 'constants/routes';
import { useMountedRef } from 'hooks/useMountedRef';
import { useOrgPath } from 'hooks/useOrgPath';
import { useCurrentUser, useTags, useUploadFile } from 'hooks';
import { toErrorMessage } from 'hooks/utils';
import { ELineChannelStatus } from 'types';
import { EOrganizationFeature } from 'types/enum';
import {
  canFetchOrganizationFeature,
  isOrganizationFeatureEnabled,
} from 'utils/organizationFeature';
import type { TGetLineFriendsResponse, TTag } from 'types';
import getFileSize from 'utils/getFileSize';
import getPosterFile from 'utils/getPosterFile';
import { previewNameInsertion } from 'utils/nameInsertion';
import { apiIssue, issueMessage, MANUAL_FRIEND_IDS_LIMIT, MANUAL_TARGET_LIMIT, PUBLISH_APPLY_RUN_LIMIT, SCENARIO_CONDITION_MAX_TAGS } from 'utils/scenarioIssues';

import { TimingModal } from './TimingModal';
import { formatJst, isTimingBehindPrevious, normalizeTimingForIndex, timingSummary } from '../duration';
import { conditionCaseLabel, conditionMatchLabels, stateLabels, stateTones } from '../labels';
import { assetOptionLabel, MessagePreview, messageTypeLabels } from '../MessagePreview';
import { ALL_ACCOUNTS_LABEL, resolveTargets, usableAccount, type ScenarioTargets } from '../targets';

// 矢印キーでの移動先が分かるよう、hoverと同じ強調をfocusにも当てる（Menu.Itemはfocusを移す）
const menuItemClass = 'grid w-full gap-0.5 px-4 py-2.5 text-left text-sm text-ink transition hover:bg-neutral-25 focus:bg-neutral-25 focus:outline-none';
const menuHintClass = 'text-xs text-muted';
const selectClass = 'h-11 w-full rounded-2xl border border-border/80 bg-white px-3 text-sm text-ink';
const labelClass = 'grid gap-2 text-xs font-semibold text-ink';
// Buttonのoutlineと同じ見た目でLinkを出す（遷移はaでないとdirty guardに載らない）
// 開始条件は「条件を満たした瞬間」しか拾えない。すでに満たしている友だちが取りこぼされることを選択時に伝える
const triggerHint: Record<string, string> = {
  friend_add: 'これから友だち追加した人が対象です。すでに友だちの人には届かないため、「友だちを手動登録」で追加してください。',
  tag_added: 'これからタグが付いた人が対象です。すでにタグが付いている人には届かないため、「友だちを手動登録」で追加してください。',
  manual: '自動では始まりません。「友だちを手動登録」から開始します。',
};
const previewLinkClass ='inline-flex h-11 items-center justify-center rounded-2xl border border-border/80 bg-white px-4 text-sm font-semibold text-ink transition hover:bg-neutral-25 aria-disabled:pointer-events-none aria-disabled:opacity-50';
const sectionTitleClass = 'text-xs font-semibold uppercase tracking-wide text-muted';
const SCENARIO_QUERY_KEY = 'scenarios';
const messageTypes = Object.entries(messageTypeLabels) as [ScenarioMessageV1['type'], string][];
type Editor = { id: number; name: string; description: string; definition: ScenarioDefinitionV1; editVersion: number; state: ScenarioState; currentRevisionId: number | null; hasUnpublishedChanges: boolean };
type ManualState = { type: 'all' | 'tag' | 'friends'; tagId: string; key: string; selected: number[]; page: number; search: string; preview?: ScenarioManualPreviewResponse; result?: ScenarioManualCommitResponse | ScenarioManualFriendsCommitResponse };
type TestState = { stepId: string; selected: number[]; page: number; search: string; names?: Record<number, string>; previewFriendId?: number; prepared?: ScenarioTestSendPrepareResponse; result?: ScenarioTestSendCommitResponse };
/** prepareが確定した宛先別の本文をプレビューへ重ね、確認内容と実際に送る内容を一致させる。 */
const withPreparedText = (messages: ScenarioMessageV1[], sent?: Array<Record<string, ScenarioJsonValue>>) =>
  sent && sent.length === messages.length
    ? messages.map((message, index) => {
        const text = sent[index]?.text;
        return message.type === 'text' && typeof text === 'string' ? { ...message, content: text } : message;
      })
    : messages;
const number = new Intl.NumberFormat('ja-JP').format;
const testStatusLabel: Record<ScenarioTestSendOutcome['status'], string> = { sent: '送信成功', failed: '送信失敗', unknown: '結果不明', not_sent: '未送信' };
const notSentReasonLabel: Record<ScenarioTestSendNotSentReason, string> = { NOT_FOLLOWING: '友だちではありません', NOT_TEST_USER: 'テストユーザーではありません', RECIPIENT_UNAVAILABLE: '送信先を利用できません', SCENARIO_ARCHIVED: 'シナリオがアーカイブ済みです', ACCOUNT_UNAVAILABLE: 'LINE公式アカウントを利用できません', DISPATCH_LEASE_LOST: '送信枠を確保できませんでした', OPERATION_DEADLINE_EXCEEDED: '制限時間内に送信を開始できませんでした', TEMPORARILY_UNAVAILABLE: '一時的に送信できませんでした' };
const testOutcomeReason = (outcome: ScenarioTestSendOutcome) => outcome.status === 'not_sent' ? notSentReasonLabel[outcome.reason] : 'reason' in outcome && outcome.reason ? outcome.reason : '';
const manualSkipReasonLabel: Record<ScenarioManualSkipReason, string> = { NOT_FOLLOWING: '友だちではありません', ACTIVE_RUN_EXISTS: '進行中の配信があります' };
const canApplyImpact = (impact?: ScenarioPublishImpact | null) => Boolean(impact?.canApplyToActiveRuns && !impact.dependencyProjectionMutationLimitExceeded);
const applyBlockReason = (impact: ScenarioPublishImpact) =>
  impact.dependencyProjectionMutationLimitExceeded ? '内部参照の更新が10,000件を超えるため、既存Runへの反映をOFFにして公開してください。'
  : impact.activeRunCount > PUBLISH_APPLY_RUN_LIMIT ? '既存Runへの反映は1,000件までです。反映OFFで公開してください。'
  : '現在位置が削除されたRunがあるため、反映OFFで公開してください。';
const expectedImpactOf = ({ activeRunCount, switchableRunCount, blockedCurrentStepRunCount, completingRunCount, skippedUnprocessedStepCount, nextStepCounts }: ScenarioPublishImpact): ScenarioPublishExpectedImpact =>
  ({ activeRunCount, switchableRunCount, blockedCurrentStepRunCount, completingRunCount, skippedUnprocessedStepCount, nextStepCounts });
/** 公開の影響を、件数の意味が読める形で並べる。 */
const impactItems = (impact: ScenarioPublishImpact) => [
  { testId: 'active', label: '進行中', value: `${number(impact.activeRunCount)}件`, hint: 'いま配信が動いている友だちの数' },
  { testId: 'switchable', label: '切替可能', value: `${number(impact.switchableRunCount)}件`, hint: '新しい内容へ切り替えられる配信' },
  { testId: 'blocked', label: '切替不可', value: `${number(impact.blockedCurrentStepRunCount)}件`, hint: 'いまのステップが新しい内容にないため、元の内容のまま進む配信' },
  { testId: 'completing', label: '完了予定', value: `${number(impact.completingRunCount)}件`, hint: '新しい内容では残りのステップがなく、完了になる配信' },
  { testId: 'skipped', label: 'スキップ', value: `${number(impact.skippedUnprocessedStepCount)}ステップ`, hint: '予定時刻を過ぎているため、送らずに飛ばすステップ' },
];

function messageFor(type: ScenarioMessageV1['type'], id = uuidv4()): ScenarioMessageV1 {
  if (type === 'text') return { id, type, content: '' };
  if (type === 'image') return { id, type, media: null };
  if (type === 'video') return { id, type, media: null };
  return { id, type, assetId: null };
}
/** タグ管理OFFの組織向け。入力は隠すが保存済みの設定は定義に残したまま送る。 */
const tagsDisabledNote = 'タグ管理は現在ご利用いただけないため、タグの設定は変更できません。保存済みの設定はそのまま残ります。';

const stepFor = (): ScenarioStepV1 => ({ id: uuidv4(), timing: null, messages: [messageFor('text')], condition: null, falseTargetStepId: null, onReachTagId: null });
/**
 * 折りたたみ時に中身を推し量れるだけの要約を作る。
 * 100ステップまで作れる画面なので、開かなくても「どのステップか」が分かることを優先する。
 */
function stepDigest(step: ScenarioStepV1): string {
  const text = step.messages.find((message) => message.type === 'text' && message.content?.trim());
  if (text && text.type === 'text' && text.content) return previewNameInsertion(text.content.trim()).slice(0, 40);
  const first = step.messages[0];
  return first ? messageTypeLabels[first.type] : 'メッセージなし';
}
const editorJson = (value: Editor) => JSON.stringify({ name: value.name, description: value.description, definition: value.definition });
const fromRecord = (record: ScenarioRecord): Editor => ({ id: record.id, name: record.name, description: record.description ?? '', definition: record.editingDefinition, editVersion: record.editVersion, state: record.state, currentRevisionId: record.currentRevisionId, hasUnpublishedChanges: record.hasUnpublishedChanges });

type MediaMessage = Extract<ScenarioMessageV1, { type: 'image' | 'video' }>;
type MediaProps = { message: MediaMessage; disabled: boolean; onChange: (value: MediaMessage) => void; onUploaded: (value: MediaMessage) => void; onUploadingChange: (uploading: boolean) => void; onError: (value: string) => void };
// タップURLは任意。空欄はnullへ戻し、ラベルだけ残らないようにする
const withMediaAction = (message: MediaMessage, field: 'actionUrl' | 'actionLabel', value: string): MediaMessage => {
  if (!message.media) return message;
  const media = { ...message.media, [field]: value.trim() ? value : null };
  return { ...message, media: media.actionUrl ? media : { ...media, actionLabel: null } } as MediaMessage;
};
// uploadは時間差で終わるため、開始時ではなく最新stateのタップURL設定を残し、
// 差し替わったファイル項目だけを取り込む（アップロード中のURL編集を巻き戻さない）
const mergeUploadedMedia = (current: MediaMessage, uploaded: MediaMessage): MediaMessage => {
  if (!uploaded.media) return current;
  const actionUrl = current.media?.actionUrl ?? null;
  return { ...current, media: { ...uploaded.media, actionUrl, actionLabel: actionUrl ? current.media?.actionLabel ?? null : null } } as MediaMessage;
};
function MediaEditor({ message, disabled, onChange, onUploaded, onUploadingChange, onError }: MediaProps) {
  const { loading, uploadToS3 } = useUploadFile();
  const upload = async (file: File) => {
    // 保存・公開の応答で編集内容が置き換わるため、進行中はupload開始も親へ通知した完了も止める
    onUploadingChange(true);
    try {
      const size = await getFileSize(file);
      if (!size.originalWidth || !size.originalHeight) throw new Error('画像・動画のサイズを取得できませんでした。');
      let objectUrl: string | undefined;
      try {
        objectUrl = message.type === 'video' ? URL.createObjectURL(file) : undefined;
        const files = objectUrl ? [file, await getPosterFile(objectUrl, file.type)] : [file];
        const uploaded = await uploadToS3({ files });
        // タップURLはupload中にも編集され得るため、ここでは持たず親が最新stateからmergeする
        const media = { type: file.type, url: uploaded[0].uploadedUrl, fileName: uploaded[0].fileName, originalWidth: size.originalWidth, originalHeight: size.originalHeight, actionUrl: null, actionLabel: null };
        onUploaded(message.type === 'video' ? { ...message, media: { ...media, poster: uploaded[1].uploadedUrl } } : { ...message, media });
      } finally { if (objectUrl) URL.revokeObjectURL(objectUrl); }
    } catch (error) {
      onError(toErrorMessage(error as Error, 'アップロードできませんでした。'));
    } finally {
      onUploadingChange(false);
    }
  };
  return (
    <div className="grid gap-2 rounded-xl bg-neutral-25 p-3 text-xs">
      <input type="file" data-testid="scenario-media-file-input" accept={message.type === 'image' ? 'image/*' : 'video/*'} disabled={loading || disabled} onChange={(event) => { const file = event.target.files?.[0]; if (file) void upload(file); }} />
      <span className="text-muted">{loading ? 'アップロード中…' : message.media?.fileName || 'ファイルを選択してください'}</span>
      {message.media && (
        <div className="grid gap-2">
          <Input value={message.media.actionUrl ?? ''} maxLength={1000} placeholder="タップ時に開くURL（任意・https）" onChange={(event) => onChange(withMediaAction(message, 'actionUrl', event.target.value))} />
          {message.media.actionUrl && (
            <Input value={message.media.actionLabel ?? ''} maxLength={40} placeholder="ボタンのラベル（省略時はもっと見る）" onChange={(event) => onChange(withMediaAction(message, 'actionLabel', event.target.value))} />
          )}
        </div>
      )}
    </div>
  );
}

type MessageProps = { message: ScenarioMessageV1; assets: TAutoReplyMessageAsset[]; references: ScenarioAssetReference[]; working: boolean; onChange: (value: ScenarioMessageV1) => void; onUploaded: (value: MediaMessage) => void; onUploadingChange: (uploading: boolean) => void; onRemove: () => void; canRemove: boolean; onError: (value: string) => void };
function MessageEditor({ message, assets, references, working, onChange, onUploaded, onUploadingChange, onRemove, canRemove, onError }: MessageProps) {
  const assetType = message.type === 'richMessage' ? 'RICH_MESSAGE' : message.type === 'cardTypeMessage' ? 'CARD_TYPE_MESSAGE' : null;
  const choices = assetType ? assets.filter(({ type }) => type === assetType) : [];
  const selectedId = assetType && 'assetId' in message ? message.assetId : null;
  const selectedMissing = selectedId !== null && !choices.some(({ id }) => id === selectedId);
  const selectedReference = selectedId === null ? undefined : references.find(({ assetId }) => assetId === selectedId);
  return (
    <div className="grid gap-3 rounded-2xl border border-border/70 p-3">
      <div className="flex gap-2">
        <select className={selectClass} value={message.type} onChange={(event) => onChange(messageFor(event.target.value as ScenarioMessageV1['type'], message.id))}>
          {messageTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <Button type="button" size="sm" variant="outline" disabled={!canRemove} title={canRemove ? undefined : 'メッセージは1件以上必要です'} onClick={onRemove}>削除</Button>
      </div>
      {message.type === 'text' && (
        <Textarea value={message.content ?? ''} maxLength={5000} placeholder="本文（{{name}}で友だちの名前を差し込めます）" onChange={(event) => onChange({ ...message, content: event.target.value })} />
      )}
      {(message.type === 'image' || message.type === 'video') && (
        <MediaEditor message={message} disabled={working} onChange={onChange} onUploaded={onUploaded} onUploadingChange={onUploadingChange} onError={onError} />
      )}
      {assetType && 'assetId' in message && (
        <select className={selectClass} value={message.assetId ?? ''} onChange={(event) => onChange({ ...message, assetId: Number(event.target.value) || null })}>
          <option value="">素材を選択してください</option>
          {selectedMissing && selectedId !== null && <option value={selectedId}>{assetOptionLabel(selectedId, selectedReference)}</option>}
          {choices.map((asset) => <option key={asset.id} value={asset.id}>{asset.name}</option>)}
        </select>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-3">
      <h3 className={sectionTitleClass}>{title}</h3>
      {children}
    </section>
  );
}

type BranchProps = { step: ScenarioStepV1; steps: ScenarioStepV1[]; tags: TTag[] };
/** 条件つきステップの分岐先を、成立／不成立の2行で読めるようにする。 */
function ConditionBranch({ step, steps, tags }: BranchProps) {
  if (!step.condition) return null;
  const index = steps.findIndex(({ id }) => id === step.id);
  const isLast = index === steps.length - 1;
  const targetIndex = step.falseTargetStepId ? steps.findIndex(({ id }) => id === step.falseTargetStepId) : -1;
  // 最終stepに次は無く、backendは完了扱いにするので「次へ」と書かない
  const falseLabel =
    targetIndex >= 0 ? `ステップ${targetIndex + 1}へ進みます（このステップは送信しません）`
    : isLast ? 'このステップは送信せず、ここで配信が終わります'
    : '次のステップへ進みます（このステップは送信しません）';
  return (
    <div className="grid gap-2 rounded-xl border border-border/60 bg-neutral-25 p-3 text-xs">
      {/* 「満たす／満たさない」は条件の種類によって指す側が入れ替わるので、条件そのものを書く */}
      <p className="flex flex-wrap items-baseline gap-2">
        <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 font-semibold text-green-800">{conditionCaseLabel(step.condition, tags, true)}</span>
        <span className="text-ink">{isLast ? 'このステップを送信して、配信は完了です' : 'このステップのメッセージを送信します'}</span>
      </p>
      <p className="flex flex-wrap items-baseline gap-2">
        <span className="shrink-0 rounded-full bg-neutral-50 px-2 py-0.5 font-semibold text-muted">{conditionCaseLabel(step.condition, tags, false)}</span>
        <span className="text-ink">{falseLabel}</span>
      </p>
    </div>
  );
}

type StepProps = { step: ScenarioStepV1; index: number; steps: ScenarioStepV1[]; tags: TTag[]; canUseTags: boolean; assets: TAutoReplyMessageAsset[]; references: ScenarioAssetReference[]; working: boolean; onChange: (value: ScenarioStepV1) => void; onEditTiming: () => void; onUploaded: (value: MediaMessage) => void; onUploadingChange: (uploading: boolean) => void; onRemove: () => void; onDuplicate: () => void; onMove: (offset: number) => void; onTest: () => void; testDisabled: boolean; onError: (value: string) => void; open: boolean; onToggle: () => void };
function StepEditor({ step, index, steps, tags, canUseTags, assets, references, working, onChange, onEditTiming, onUploaded, onUploadingChange, onRemove, onDuplicate, onMove, onTest, testDisabled, onError, open, onToggle }: StepProps) {
  const conditionType = step.condition?.type ?? '';
  const condition = (type: string): ScenarioConditionV1 | null =>
    type === 'tag_all' || type === 'tag_any' || type === 'tag_none' ? { type, tagIds: [] } : null;
  const toNumber = (value: string) => (value === '' ? null : Number(value));
  const toggleConditionTag = (tagId: number) => {
    const current = step.condition!;
    const selected = current.tagIds.includes(tagId);
    // 上限に達していたら、外す操作だけ受け付ける
    if (!selected && current.tagIds.length >= SCENARIO_CONDITION_MAX_TAGS) return;
    const tagIds = selected ? current.tagIds.filter((id) => id !== tagId) : [...current.tagIds, tagId];
    onChange({ ...step, condition: { ...current, tagIds } });
  };

  return (
    // 狭い画面では縦線の余白を本文へ回し、番号はカード見出しの「ステップ N」が担う
    <li className="relative grid gap-2 sm:pl-14">
      <span aria-hidden className="absolute left-0 top-0 hidden h-8 w-8 items-center justify-center rounded-full bg-accent text-xs font-semibold text-white sm:flex">{index + 1}</span>
      {/* バッジ中心(16px)に2pxで重ねる。1px＋translateだとサブピクセルに割れて見えなくなる */}
      {index < steps.length - 1 && <span aria-hidden className="absolute -bottom-4 left-[15px] top-9 hidden w-0.5 bg-accent/30 sm:block" />}
      <p className="flex items-start text-xs font-semibold text-primary-800 sm:pt-2">{timingSummary(step.timing, index)}</p>
      <article className="grid gap-5 rounded-2xl border border-border/70 bg-white p-4">
        <header className="flex flex-wrap items-center justify-between gap-2">
          {/* 全ステップを開いたままだと20件で1.5万pxを超えるため、見出しを開閉ボタンにして
              閉じている間は要約だけを見せる */}
          <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left" aria-expanded={open} aria-controls={`step-body-${step.id}`} onClick={onToggle}>
            <span aria-hidden className={`shrink-0 text-xs text-muted transition ${open ? 'rotate-90' : ''}`}>▶</span>
            <h2 className="shrink-0 font-semibold text-ink">ステップ {index + 1}</h2>
            {!open && (
              <span className="flex min-w-0 items-center gap-2 text-xs text-muted">
                <span className="shrink-0">{timingSummary(step.timing, index)}</span>
                {step.condition && <span className="shrink-0 rounded-full bg-accent-soft px-2 py-0.5 font-semibold text-primary-800">条件つき</span>}
                <span className="min-w-0 truncate">{stepDigest(step)}</span>
                {step.messages.length > 1 && <span className="shrink-0">ほか{step.messages.length - 1}件</span>}
              </span>
            )}
          </button>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" aria-label={`ステップ ${index + 1} を上へ移動`} disabled={!index} onClick={() => onMove(-1)}>↑</Button>
            <Button type="button" size="sm" variant="outline" aria-label={`ステップ ${index + 1} を下へ移動`} disabled={index === steps.length - 1} onClick={() => onMove(1)}>↓</Button>
            <Button type="button" size="sm" variant="outline" aria-label={`ステップ ${index + 1} を複製`} onClick={onDuplicate}>複製</Button>
            <Button type="button" size="sm" variant="outline" disabled={testDisabled} onClick={onTest}>テスト送信</Button>
            {/* 破壊操作なので、頻用のテスト送信とは間隔を空けて危険色で区別する */}
            <Button type="button" size="sm" variant="outline" className="ml-2 border-danger-border text-danger hover:bg-danger-soft" aria-label={`ステップ ${index + 1} を削除`} onClick={onRemove}>削除</Button>
          </div>
        </header>
        {/* display:grid はhidden属性より強いので、閉じている間はそもそも描画しない */}
        {open && <div id={`step-body-${step.id}`} className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,18rem)]">
          <div className="grid gap-5">
            <Section title="配信タイミング">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-ink">{timingSummary(step.timing, index)}</span>
                <Button type="button" size="sm" variant="outline" onClick={onEditTiming}>{step.timing ? '配信タイミングを変更' : '配信タイミングを設定'}</Button>
              </div>
              {/* 配信は必ずステップ順に進むため、前のステップより早い指定は指定時刻どおりには送れない */}
              {isTimingBehindPrevious(steps, index) && <p className="mt-2 text-xs text-amber-700">前のステップより早い指定です。実際には前のステップが送られた直後に送られます。</p>}
            </Section>
            <Section title="送信条件">
              {!canUseTags && <p className="text-xs text-muted">{tagsDisabledNote}</p>}
              {canUseTags && <div className="grid gap-3 md:grid-cols-2">
                <label className={labelClass}>条件<select className={selectClass} value={conditionType} onChange={(e) => onChange({ ...step, condition: condition(e.target.value), falseTargetStepId: null })}><option value="">なし（必ず送信）</option>{(Object.keys(conditionMatchLabels) as ScenarioConditionMatchV1[]).map((type) => <option key={type} value={type}>{conditionMatchLabels[type]}</option>)}</select></label>
                {step.condition && (
                  <label className={labelClass}>条件不成立時<select className={selectClass} value={step.falseTargetStepId ?? ''} onChange={(e) => onChange({ ...step, falseTargetStepId: e.target.value || null })}><option value="">次のステップへ</option>{steps.slice(index + 1).map((target, offset) => <option key={target.id} value={target.id}>ステップ {index + offset + 2}</option>)}</select></label>
                )}
              </div>}
              {canUseTags && step.condition && (
                <div className="grid gap-2" role="group" aria-label={`ステップ ${index + 1} の条件タグ`}>
                  <p className={labelClass}>{`条件タグ（最大${SCENARIO_CONDITION_MAX_TAGS}件）`}</p>
                  {tags.length ? <TagSelector tags={tags} selectedTagIds={step.condition.tagIds} onToggle={toggleConditionTag} /> : <p className="text-xs text-muted">タグがありません。</p>}
                  {step.condition.tagIds.length >= SCENARIO_CONDITION_MAX_TAGS && <p className="text-xs text-muted">{`タグは${SCENARIO_CONDITION_MAX_TAGS}件まで選べます。`}</p>}
                </div>
              )}
              {canUseTags && <ConditionBranch step={step} steps={steps} tags={tags} />}
            </Section>
            <Section title={`メッセージ（${step.messages.length} / 5件）`}>
              {step.messages.map((message, messageIndex) => (
                <MessageEditor key={message.id} message={message} assets={assets} references={references} working={working} canRemove={step.messages.length > 1} onError={onError} onUploaded={onUploaded} onUploadingChange={onUploadingChange} onRemove={() => onChange({ ...step, messages: step.messages.filter((_, i) => i !== messageIndex) })} onChange={(value) => onChange({ ...step, messages: step.messages.map((item, i) => i === messageIndex ? value : item) })} />
              ))}
              <Button type="button" size="sm" variant="outline" disabled={step.messages.length >= 5} onClick={() => onChange({ ...step, messages: [...step.messages, messageFor('text')] })}>メッセージを追加（最大5件）</Button>
            </Section>
            <Section title="送信後の処理">
              {!canUseTags && <p className="text-xs text-muted">{tagsDisabledNote}</p>}
              {canUseTags && <label className={labelClass}>送信成功時に付けるタグ<select className={selectClass} value={step.onReachTagId ?? ''} onChange={(e) => onChange({ ...step, onReachTagId: toNumber(e.target.value) })}><option value="">なし</option>{tags.map((tag) => <option key={tag.id} value={tag.id}>{tag.name}</option>)}</select></label>}
            </Section>
          </div>
          <MessagePreview step={index + 1} messages={step.messages} assets={assets} references={references} />
        </div>}
      </article>
    </li>
  );
}

type PickerProps = { label: string; data?: TGetLineFriendsResponse; loading: boolean; error: Error | null; selected: number[]; max: number; page: number; search: string; onChange: (ids: number[]) => void; onPage: (page: number) => void; onSearch: (search: string) => void };
function FriendPicker({ label, data, loading, error, selected, max, page, search, onChange, onPage, onSearch }: PickerProps) {
  return <div className="grid gap-2">
    <Input aria-label={`${label}を検索`} placeholder="名前で検索" value={search} onChange={(event) => onSearch(event.target.value)} />
    {error && <Alert>{toErrorMessage(error as Error, `${label}を読み込めませんでした。`)}</Alert>}
    <p className="text-xs text-muted">{selected.length} / {max}人選択</p>
    <div className="max-h-72 overflow-y-auto rounded-xl border border-border p-2">{error ? <p className="p-2 text-muted">候補を表示できません。</p> : loading ? <p className="p-2 text-muted">読み込み中…</p> : data?.data.length ? data.data.map((friend) => <label key={friend.id} className="flex items-center gap-3 rounded-lg p-2 hover:bg-neutral-25"><input type="checkbox" checked={selected.includes(friend.id)} disabled={!selected.includes(friend.id) && selected.length >= max} onChange={(event) => onChange(event.target.checked ? [...selected, friend.id] : selected.filter((id) => id !== friend.id))} /><span>{friend.lineName}（#{friend.id}）</span></label>) : <p className="p-2 text-muted">対象の友だちがいません。</p>}</div>
    <div className="flex items-center justify-between"><Button type="button" size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => onPage(page - 1)}>前へ</Button><span className="text-xs">{page} / {Math.max(data?.totalPage ?? 1, 1)}ページ</span><Button type="button" size="sm" variant="outline" disabled={page >= (data?.totalPage ?? 1) || loading} onClick={() => onPage(page + 1)}>次へ</Button></div>
  </div>;
}

export default function ScenarioEdit() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const orgPathFor = useOrgPath();
  // 保存の後に続く呼び出しも、画面を開いた組織へ送る
  const organizationId = useOrganizationScopeOrNull()?.organizationId ?? null;
  const mountedRef = useMountedRef();
  const scenarioId = Number(useParams().scenarioId);
  const validId = Number.isSafeInteger(scenarioId) && scenarioId > 0;
  const detailQuery = useQuery({ queryKey: [SCENARIO_QUERY_KEY, 'detail', scenarioId], queryFn: () => getScenario(scenarioId), enabled: validId, retry: false });
  const { data: currentUser } = useCurrentUser();
  // タグ管理OFFでも編集・保存はでき、保存済みのタグ設定は定義に残したまま送る
  const canUseTags = isOrganizationFeatureEnabled(currentUser, EOrganizationFeature.TAG_MANAGEMENT);
  const { tags, error: tagsError } = useTags({ enabled: canFetchOrganizationFeature(currentUser, EOrganizationFeature.TAG_MANAGEMENT) });
  const tagsNotice = canUseTags ? tagsError : undefined;
  const assetsQuery = useQuery({ queryKey: [queryKeys.AUTO_REPLY_MESSAGE_ASSETS, 'scenario-editor'], queryFn: () => getAutoReplyMessageAssets() });
  const [editor, setEditor] = useState<Editor | null>(null);
  const [baseline, setBaseline] = useState('');
  const [busy, setBusy] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [success, setSuccess] = useState<string>();
  const [publish, setPublish] = useState<{ expectedEditVersion: number; impact: ScenarioPublishImpact | null; apply: boolean }>();
  const [manual, setManual] = useState<ManualState>();
  const [test, setTest] = useState<TestState>();
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmRemoveStep, setConfirmRemoveStep] = useState<number | null>(null);
  // 閉じているステップのID。既定は展開のままにして、長くなったら畳める逃げ道を用意する。
  // 既定を閉じにすると「開いてすぐ書き始める」通常の編集が一手増えるため。
  const [closedStepIds, setClosedStepIds] = useState<string[]>([]);
  const toggleStep = (id: string) => setClosedStepIds((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  const [timingStepIndex, setTimingStepIndex] = useState<number | null>(null);
  // upload中は保存・公開の応答でmediaが消えるため、親が本数を数えて操作を止める
  const [uploadingCount, setUploadingCount] = useState(0);
  const changeUploading = (uploading: boolean) => setUploadingCount((count) => Math.max(0, count + (uploading ? 1 : -1)));
  const allowLeave = useRef(false);
  // guardの再実行はguarded単位なので、確認文だけは最新の理由をrefから読む
  const leavePromptRef = useRef('未保存の変更を破棄しますか？');
  const navigateRef = useRef(navigate);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);
  // 対象アカウントは作成時に決まり以後変わらないため、編集state(Editor)ではなくdetailから読む
  const detail = detailQuery.data?.detail;
  const targets: ScenarioTargets = detail ? resolveTargets(detail) : { allAccounts: true, accounts: [] };
  // 友だち候補はアカウント限定シナリオのときだけ絞り込める。複数対象は組織全体から選ぶ
  const scopedChannelId = targets.accounts.length === 1 ? targets.accounts[0].id : null;

  useEffect(() => {
    const detail = detailQuery.data?.detail;
    if (!detail || editor?.id === detail.id) return;
    const value: Editor = { id: detail.id, name: detail.name, description: detail.description ?? '', definition: detail.definition, editVersion: detail.editVersion, state: detail.state, currentRevisionId: detail.currentRevisionId, hasUnpublishedChanges: detail.hasUnpublishedChanges };
    setEditor(value); setBaseline(editorJson(value));
  }, [detailQuery.data, editor?.id]);

  const dirty = Boolean(editor && editorJson(editor) !== baseline);
  const uploading = uploadingCount > 0;
  // upload中はeditorがまだ変わらずdirty=falseになり得るので、離脱guardの条件に含める
  const guarded = dirty || uploading;
  leavePromptRef.current = uploading ? 'アップロード中のファイルがあります。破棄して移動しますか？' : '未保存の変更を破棄しますか？';
  useUnsavedInputGuard(() => guarded);
  useEffect(() => {
    if (!guarded) return;
    allowLeave.current = false;
    const marker = uuidv4();
    const markerKey = '__scenarioDirtyGuard';
    window.history.pushState({ ...window.history.state, [markerKey]: marker }, '', window.location.href);
    let exiting = false;
    let restoring = false;
    let dropPending: (() => void) | undefined;
    const unload = (event: BeforeUnloadEvent) => {
      if (allowLeave.current) return;
      event.preventDefault(); event.returnValue = '';
    };
    const stop = () => {
      window.removeEventListener('beforeunload', unload);
      window.removeEventListener('popstate', pop);
      document.removeEventListener('click', click, true);
    };
    // 離脱確認用に積んだhistory entryを1つ戻してから、SPA遷移で目的のページへ進む。
    const exit = (to?: string) => {
      exiting = true; allowLeave.current = true; stop();
      if (to) {
        const go = () => navigateRef.current(to);
        window.addEventListener('popstate', go, { once: true });
        dropPending = () => window.removeEventListener('popstate', go);
      }
      window.history.back();
    };
    const pop = () => {
      if (restoring) { restoring = false; return; }
      if (window.confirm(leavePromptRef.current)) exit();
      else { restoring = true; window.history.forward(); }
    };
    const click = (event: MouseEvent) => {
      const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin || url.href === window.location.href) return;
      event.preventDefault(); event.stopPropagation();
      if (window.confirm(leavePromptRef.current)) exit(`${url.pathname}${url.search}${url.hash}`);
    };
    window.addEventListener('beforeunload', unload);
    window.addEventListener('popstate', pop);
    document.addEventListener('click', click, true);
    return () => {
      stop(); dropPending?.();
      if (!exiting && window.history.state?.[markerKey] === marker) window.history.back();
    };
  }, [guarded]);

  const friendChannelParam = () => (scopedChannelId === null ? {} : { lineChannelId: scopedChannelId });
  const testFriendsQuery = useQuery({ queryKey: [queryKeys.LINE_FRIENDS, 'scenario-test', scopedChannelId, test?.page, test?.search], queryFn: () => listLineFriends({ currentPage: test!.page, pageSize: 100, ...friendChannelParam(), friendStatus: 'NORMAL', isTestUser: true, ...(test!.search.trim() ? { search: test!.search.trim() } : {}) }), enabled: Boolean(test && editor && !test.prepared && !test.result), retry: false });
  const manualFriendsQuery = useQuery({ queryKey: [queryKeys.LINE_FRIENDS, 'scenario-manual', scopedChannelId, manual?.page, manual?.search], queryFn: () => listLineFriends({ currentPage: manual!.page, pageSize: 100, ...friendChannelParam(), friendStatus: 'NORMAL', ...(manual!.search.trim() ? { search: manual!.search.trim() } : {}) }), enabled: Boolean(manual?.type === 'friends' && editor && !manual.result), retry: false });
  const applyRecord = (record: ScenarioRecord) => {
    const value = fromRecord(record);
    setEditor(value); setBaseline(editorJson(value));
    queryClient.invalidateQueries({ queryKey: [SCENARIO_QUERY_KEY] });
    return value;
  };
  const perform = async <Result,>(busyKey: string, fallback: string, operation: () => Promise<Result>, onError?: (error: unknown) => boolean) => {
    setBusy(busyKey); setNotice(undefined);
    try {
      const result = await operation();
      // 画面を離れた後は続きの遷移・API 呼び出しへ進ませない
      return mountedRef.current ? result : undefined;
    } catch (error) {
      if (!onError?.(error)) setNotice(issueMessage(error, fallback));
    } finally {
      setBusy(undefined);
    }
  };
  const saveDraft = async (fallback = '下書きを保存できませんでした。') => {
    if (!editor) return;
    // 画面上に未保存の変更がある時だけPATCHする。保存済みならeditVersionもcanonical内容も既に最新。
    if (!dirty) return editor;
    const name = editor.name.trim();
    if (!name || name.length > 100 || editor.description.length > 500) {
      setNotice('シナリオ名と説明の文字数を確認してください。');
      return;
    }
    return perform('save', fallback, async () =>
      applyRecord(await updateScenario(editor.id, {
        expectedEditVersion: editor.editVersion,
        name,
        description: editor.description || null,
        definition: editor.definition,
      })),
    );
  };

  const updateSteps = (steps: ScenarioStepV1[]) => {
    if (!editor) return;
    const indexes = new Map(steps.map((step, index) => [step.id, index]));
    // 先頭には前のステップが無いので、並べ替え・削除でrelativeが来たら待機値を保ったままelapsedへ読み替える。
    // 逆に「登録直後」(elapsed 0)が先頭から外れたら、同じ意味で選択肢としても存在するrelative 0へ読み替える
    const safe = steps.map((step, index) => ({
      ...step,
      timing: normalizeTimingForIndex(step.timing, index),
      falseTargetStepId: step.falseTargetStepId && (indexes.get(step.falseTargetStepId) ?? -1) > index ? step.falseTargetStepId : null,
    }));
    setEditor({ ...editor, definition: { ...editor.definition, steps: safe } });
  };
  /** 入力済みのステップを1クリックで失わないよう、中身があるときだけ確認を挟む。 */
  const requestRemoveStep = (index: number) => {
    if (!editor) return;
    const step = editor.definition.steps[index];
    const filled = step.messages.some((message) => (message.type === 'text' ? Boolean(message.content?.trim()) : true));
    if (!filled) return updateSteps(editor.definition.steps.filter((_, i) => i !== index));
    setConfirmRemoveStep(index);
  };
  const removeStep = (index: number) => {
    if (!editor) return;
    updateSteps(editor.definition.steps.filter((_, i) => i !== index));
    setConfirmRemoveStep(null);
  };
  /** 似たステップを作り直さずに済むよう、直後へ複製する。IDは重複させない。 */
  const duplicateStep = (index: number) => {
    if (!editor) return;
    const source = editor.definition.steps[index];
    const copy: ScenarioStepV1 = {
      ...source,
      id: uuidv4(),
      messages: source.messages.map((message) => ({ ...message, id: uuidv4() })),
      // 遷移先は複製元の指す先が意味を持たなくなるため引き継がない
      falseTargetStepId: null,
    };
    const steps = [...editor.definition.steps];
    steps.splice(index + 1, 0, copy);
    updateSteps(steps);
    setClosedStepIds((current) => current.filter((value) => value !== copy.id));
  };
  /** 末尾追加＋↑連打を避けるため、指定位置の直後へ空のステップを差し込む。 */
  const insertStepAfter = (index: number) => {
    if (!editor) return;
    const added = stepFor();
    const steps = [...editor.definition.steps];
    steps.splice(index + 1, 0, added);
    updateSteps(steps);
    setClosedStepIds((current) => current.filter((value) => value !== added.id));
  };
  // アップロード完了は時間差で届くため、開始時のstepsスナップショットではなく最新stateへ適用する。
  // 同じid・typeのメッセージが残っている場合だけmediaを差し替え、削除・種類変更済みなら何もしない
  const applyUploadedMessage = (value: MediaMessage) =>
    setEditor((current) => current && ({ ...current, definition: { ...current.definition, steps: current.definition.steps.map((step) => ({ ...step, messages: step.messages.map((message) => (message.id === value.id && message.type === value.type ? mergeUploadedMedia(message as MediaMessage, value) : message)) })) } }));
  // プレビューはサーバの保存内容を描くので、未保存のまま遷移しても書きかけが映らない。
  // 離脱ガードの「破棄しますか？」で編集を失う事故も起きるため、公開と同じく先に保存する
  const openPreview = async () => {
    const saved = await perform('preview-open', 'プレビューを表示できませんでした。', () => saveDraft('プレビューを表示できませんでした。'));
    if (!saved) return;
    allowLeave.current = true;
    navigate(orgPathFor(routes.SCENARIO_PREVIEW.replace(':scenarioId', String(saved.id))));
  };

  const openPublish = async () => {
    // 公開に必須の内容が無いうちは確認画面へ進めない（API検証より前にUIで止める）
    const missing = [!editor?.definition.trigger && '開始条件', !editor?.definition.steps.length && 'ステップ'].filter((item): item is string => typeof item === 'string');
    if (missing.length) { setNotice(`公開するには${missing.join('と')}の設定が必要です。`); return; }
    const saved = await saveDraft('公開内容を確認できませんでした。');
    if (!saved) return;
    const response = await perform('preview', '公開内容を確認できませんでした。', () => getScenario(saved.id, { includePublishPreview: true }, organizationId));
    if (!response) return;
    if (response.detail.editVersion !== saved.editVersion) {
      setNotice('確認中に別の更新が入りました。ページを再読み込みしてから公開してください。'); return;
    }
    setPublish({ expectedEditVersion: response.detail.editVersion, impact: response.detail.publishPreview, apply: false });
  };

  const commitPublish = async () => {
    if (!editor || !publish) return;
    const request = publish.apply
      ? { expectedEditVersion: publish.expectedEditVersion, applyToActiveRuns: true as const, expectedImpact: expectedImpactOf(publish.impact!) }
      : { expectedEditVersion: publish.expectedEditVersion, applyToActiveRuns: false as const };
    const response = await perform('publish', '公開できませんでした。件数を再確認してください。', () => publishScenario(editor.id, request), (error) => {
      const issue = apiIssue(error);
      if (issue.code === 'DEPENDENCY_PROJECTION_MUTATION_LIMIT_EXCEEDED') {
        // trigger付きは自動開始のadmission超過で、既存Runへの反映可否とは別枠。反映OFFの案内へ倒さない。
        if (typeof issue.trigger !== 'string' && publish.impact) {
          setPublish({ ...publish, apply: false, impact: { ...publish.impact, dependencyProjectionMutationLimitExceeded: true, canApplyToActiveRuns: false } });
        }
        setNotice(issueMessage(error, '内部参照の更新が上限を超えました。')); return true;
      }
      if ((issue.code !== 'PUBLISH_PREVIEW_CHANGED' && issue.code !== 'ACTIVE_RUN_LIMIT_EXCEEDED') || !issue.details) return false;
      const impact = issue.details as ScenarioPublishImpact;
      setPublish({ ...publish, impact, apply: publish.apply && canApplyImpact(impact) });
      setNotice(issueMessage(error, '進行中の配信件数が変わりました。')); return true;
    });
    if (!response) return;
    applyRecord(response.scenario); setPublish(undefined); setSuccess('公開しました。');
  };

  const runAction = async (action: 'pause' | 'resume' | 'archive' | 'restore') => {
    if (!editor) return;
    if (action === 'archive') setConfirmArchive(false);
    const record = await perform(action, '状態を更新できませんでした。', () => changeScenarioState(editor.id, { action, expectedState: editor.state }));
    if (!record) return;
    // archive後は編集も保存もできなくなるため、未保存の変更は破棄してサーバの内容へ揃える。
    // 他のstate変更は編集を続けられるので、書きかけをそのまま残す
    if (action === 'archive') {
      applyRecord(record);
    } else {
      setEditor({ ...editor, state: record.state });
      queryClient.invalidateQueries({ queryKey: [SCENARIO_QUERY_KEY] });
    }
    setSuccess('状態を更新しました。');
  };

  const manualTarget = () => manual?.type === 'tag' ? { type: 'tag' as const, tagId: Number(manual.tagId) } : { type: 'all' as const };
  const previewManual = async () => {
    if (!editor || !manual || manual.type === 'friends' || (manual.type === 'tag' && !manual.tagId)) return;
    const preview = await perform('manual-preview', '対象件数を取得できませんでした。', () => manualEnrollScenario(editor.id, { mode: 'preview', target: manualTarget() }));
    if (preview) setManual({ ...manual, preview, result: undefined });
  };
  const commitManual = async () => {
    if (!editor || !manual || (manual.type === 'friends' ? !manual.selected.length : !manual.preview)) return;
    const operation = manual.type === 'friends'
      ? () => manualEnrollScenario(editor.id, { mode: 'commit', target: { type: 'friends', friendIds: manual.selected } }, manual.key)
      : () => manualEnrollScenario(editor.id, { mode: 'commit', target: manualTarget(), expectedTargetCount: manual.preview!.targetCount, expectedStartableCount: manual.preview!.startableCount }, manual.key);
    const result = await perform<ScenarioManualCommitResponse | ScenarioManualFriendsCommitResponse>('manual-commit', '手動登録できませんでした。', operation, (error) => {
      const issue = apiIssue(error);
      if (issue.code !== 'MANUAL_PREVIEW_CHANGED' || !issue.details || manual.type === 'friends') return false;
      setManual({ ...manual, preview: issue.details as ScenarioManualPreviewResponse });
      setNotice(issueMessage(error, '対象件数が変わりました。')); return true;
    });
    if (result) setManual({ ...manual, result });
  };

  const prepareTest = async () => {
    if (!test || test.selected.length < 1 || test.selected.length > 10) return;
    const saved = await saveDraft('テスト送信を準備できませんでした。');
    if (!saved) return;
    const prepared = await perform('test-prepare', 'テスト送信を準備できませんでした。', () => testSendScenario(saved.id, { mode: 'prepare', expectedEditVersion: saved.editVersion, stepId: test.stepId, friendIds: test.selected }, organizationId));
    // prepare後は候補queryが無効になるため、宛先名はこの時点で控えておく
    const names = Object.fromEntries((testFriendsQuery.data?.data ?? []).filter(({ id }) => test.selected.includes(id)).map(({ id, lineName }) => [id, lineName]));
    if (prepared) setTest({ ...test, names, prepared, result: undefined });
  };
  const commitTest = async () => {
    if (!editor || !test?.prepared) return;
    const result = await perform('test-commit', 'テスト送信に失敗しました。', () => testSendScenario(editor.id, { mode: 'commit', testSendToken: test.prepared!.testSendToken }, organizationId), (error) => {
      if (!['TEST_RECIPIENTS_CHANGED', 'TEST_TOKEN_EXPIRED', 'INVALID_TEST_TOKEN'].includes(apiIssue(error).code ?? '')) return false;
      setTest({ ...test, prepared: undefined, result: undefined });
      setNotice(issueMessage(error, '確認内容が無効になりました。')); return true;
    });
    if (result) setTest({ ...test, result });
  };

  if (!validId) return <><Alert>シナリオIDが正しくありません。</Alert></>;
  if (detailQuery.error) return <><Alert>{toErrorMessage(detailQuery.error, 'シナリオを読み込めませんでした。')}</Alert></>;
  if (detailQuery.isPending || !editor) return <><p className="p-6 text-sm text-muted">読み込み中…</p></>;

  const setDefinition = (definition: ScenarioDefinitionV1) => setEditor({ ...editor, definition });
  // upload中の保存・公開はmediaを取りこぼすため、他の実行中操作と同じく止める
  const working = Boolean(busy) || uploading;
  // 全アカウント対象は共通のアカウント状態を持たないため、送信可否は宛先ごとにserverが判定する
  const allAccounts = targets.allAccounts;
  const unusableTargets = targets.accounts.filter(({ status }) => !usableAccount(status));
  // 対象が複数あるときは1つでも使えれば操作を許し、宛先ごとの可否はserverへ委ねる
  // テスト送信の確認は、LINE送信用JSONではなくステップと同じ送信プレビューで見せる。
  // ただし差し込み後の本文は宛先ごとに変わるので、prepareが返した宛先別の確定内容を重ねる
  const testStepIndex = test && editor ? editor.definition.steps.findIndex(({ id }) => id === test.stepId) : -1;
  const testStep = testStepIndex >= 0 ? editor!.definition.steps[testStepIndex] : undefined;
  const testRecipients = test?.prepared?.recipients ?? [];
  // 選び直し→再prepareで宛先が変わった場合に、残ったpreviewFriendIdで差し込み前本文へ落ちないよう実在検証する
  const testPreviewFriendId = testRecipients.some(({ friendId }) => friendId === test?.previewFriendId) ? test!.previewFriendId : testRecipients[0]?.friendId;
  const testPreviewMessages = testStep ? withPreparedText(testStep.messages, testRecipients.find(({ friendId }) => friendId === testPreviewFriendId)?.messages) : [];
  const accountUnavailable = !allAccounts && targets.accounts.length > 0 && unusableTargets.length === targets.accounts.length;
  const singleTarget = !allAccounts && targets.accounts.length === 1 ? targets.accounts[0] : undefined;
  const accountNotice = allAccounts ? undefined
    : singleTarget
      ? singleTarget.status === ELineChannelStatus.PAUSED ? 'LINE公式アカウントが停止中のため、手動登録とテスト送信を利用できません。'
        : singleTarget.status === ELineChannelStatus.UNLINKED ? 'LINE公式アカウントが連携解除済みのため、手動登録とテスト送信を利用できません。'
          : accountUnavailable ? 'LINE公式アカウントの状態を確認できないため、手動登録とテスト送信を利用できません。' : undefined
      : accountUnavailable ? '対象のLINE公式アカウントがすべて停止中または連携解除済みのため、手動登録とテスト送信を利用できません。'
        : unusableTargets.length ? `${unusableTargets.map(({ label }) => label).join('、')}は停止中または連携解除済みです。このアカウントの友だちには配信されません。` : undefined;
  const manualAllLabel = allAccounts ? '組織の友だち全員' : singleTarget ? 'このアカウントの友だち全員' : '対象アカウントの友だち全員';
  const publishedVersionLabel = detail?.currentRevisionSummary ? `第${detail.currentRevisionSummary.publishNumber}版（公開済み）` : '公開済みの版';
  // 部分集合対象では候補一覧を対象アカウントへ絞れない（検索APIは単一アカウント指定のみ）ため、
  // 対象外の友だちを選ぶとprepare全体が失敗することを先に案内する
  const testPickerNotice = `${allAccounts ? '組織のいずれかのアカウント' : singleTarget ? 'このLINE公式アカウント' : '対象のいずれかのアカウント'}をフォロー中のテストユーザーから1〜10人選択してください。${!allAccounts && !singleTarget ? '一覧には対象外のアカウントの友だちも表示されます。対象外の友だちを含めると確認できません。' : ''}`;
  const backTo = orgPathFor(allAccounts || !targets.accounts.length ? routes.SCENARIOS : `${routes.SCENARIOS}?lineChannelId=${targets.accounts[0].id}`);
  const pageNotice = publish || manual || test ? undefined : notice;

  return (
    <>
      <section className={`grid w-full gap-6 ${pageCardClasses.regular}`}>
        <div className="min-w-0">
          <Link className="text-sm text-accent" to={backTo}>← 一覧へ</Link>
          <h1 className="mt-2 text-2xl font-display text-ink">ステップ配信を編集</h1>
        </div>
        {/* ステップが増えるとページが数万pxになり、最上部だけに置くと保存も未保存表示も画面外へ出る。
            スクロール位置に関わらず状態と保存・公開へ手が届くよう貼り付ける。
            見出しまで貼り付けるとスマホで画面の半分近くを塞ぐので、固定するのは状態と操作だけにする。
            lg未満はMobileTopBar(sticky top-0)が出るため、その高さぶん下げて重なりを避ける。
            sm未満は外枠カードの余白が無いので、AppLayoutの左右余白(--app-px)ぶん広げて画面の端までつなげる */}
        <header className="sticky top-[var(--mobile-topbar-h)] z-30 -mx-[var(--app-px)] flex flex-wrap items-center justify-between gap-3 border-y border-border/60 bg-white/90 px-[var(--app-px)] py-3 backdrop-blur-sm sm:-mx-7 sm:px-7 lg:top-0">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Pill tone={stateTones[editor.state]}>{stateLabels[editor.state]}</Pill>
            <Pill tone={dirty ? 'warning' : 'neutral'}>{dirty ? '未保存' : '保存済み'}</Pill>
            {editor.hasUnpublishedChanges && <Pill tone="accent">未公開の変更あり</Pill>}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* navigate()はdirty guard（a[href]のcapture click）を通らないので、必ずLinkで遷移する。
                そのcapture clickはLinkのonClickより先に遷移を確定してしまうため、無効時はanchorを描画しない */}
            {/* スマホは固定バーの高さを詰めるため「…」メニューへ逃がす（lg未満では出さない） */}
            <span className="hidden lg:block">
              {editor.definition.steps.length
                ? <Button type="button" variant="outline" loading={busy === 'preview-open'} onClick={() => void openPreview()}>一括プレビュー</Button>
                : <span className={previewLinkClass} aria-disabled>一括プレビュー</span>}
            </span>
            <Button type="button" variant="outline" loading={busy === 'save'} disabled={!dirty || working || editor.state === 'archived'} onClick={() => void saveDraft().catch(() => undefined)}>下書きを保存</Button>
            <Button type="button" loading={busy === 'preview'} disabled={working || editor.state === 'archived'} onClick={() => void openPublish()}>公開する</Button>
            <Menu as="div" className="relative">
              <Menu.Button
                aria-label="その他の操作"
                disabled={working}
                className="flex h-11 w-11 items-center justify-center rounded-2xl border border-border/80 bg-white text-lg leading-none text-muted transition hover:text-ink disabled:opacity-50"
              >
                …
              </Menu.Button>
              <Menu.Items className="absolute right-0 z-20 mt-2 w-56 overflow-hidden rounded-2xl border border-border/70 bg-white py-1 shadow-soft focus:outline-none">
                {/* lg以上はヘッダーにボタンとして出ているので、ここへは出さない */}
                <Menu.Item>
                  <button type="button" className={`${menuItemClass} lg:hidden disabled:opacity-50`} disabled={!editor.definition.steps.length || working} onClick={() => void openPreview()}>
                    一括プレビュー
                    <span className={menuHintClass}>全ステップの内容を続けて確認します</span>
                  </button>
                </Menu.Item>
                {editor.state === 'active' && (
                  <Menu.Item>
                    <button type="button" className={menuItemClass} onClick={() => void runAction('pause')}>
                      一時停止する
                      <span className={menuHintClass}>新しい配信を止めます（予定は消えません）</span>
                    </button>
                  </Menu.Item>
                )}
                {editor.state === 'paused' && (
                  <Menu.Item>
                    <button type="button" className={menuItemClass} onClick={() => void runAction('resume')}>
                      配信を再開する
                      <span className={menuHintClass}>停止中に過ぎた分から順に配信します</span>
                    </button>
                  </Menu.Item>
                )}
                {editor.state !== 'archived' ? (
                  <Menu.Item>
                    <button type="button" className={`${menuItemClass} text-danger`} onClick={() => setConfirmArchive(true)}>
                      アーカイブする
                      <span className={menuHintClass}>進行中の配信を取り消して一覧から隠します</span>
                    </button>
                  </Menu.Item>
                ) : (
                  <Menu.Item>
                    <button type="button" className={menuItemClass} onClick={() => void runAction('restore')}>
                      アーカイブから戻す
                      <span className={menuHintClass}>取り消した配信は元に戻りません</span>
                    </button>
                  </Menu.Item>
                )}
              </Menu.Items>
            </Menu>
          </div>
        </header>
        {(pageNotice || tagsNotice || assetsQuery.error) && <Alert>{pageNotice || tagsNotice || toErrorMessage(assetsQuery.error, '素材を読み込めませんでした。')}</Alert>}
        {accountNotice && <Alert>{accountNotice}</Alert>}
        <fieldset disabled={editor.state === 'archived' || working} className="grid gap-6">
        <div className="grid gap-4 rounded-2xl border border-border/70 bg-white p-4 md:grid-cols-2">
          <div className={`${labelClass} md:col-span-2`}>対象アカウント（変更できません）
            <div className="flex max-h-32 flex-wrap items-center gap-2 overflow-y-auto rounded-2xl bg-neutral-25 px-3 py-2.5">
              {allAccounts
                ? <span className="text-sm font-normal text-ink">{ALL_ACCOUNTS_LABEL}</span>
                : targets.accounts.length
                  ? targets.accounts.map((target) => <Pill key={target.id} tone={usableAccount(target.status) ? 'neutral' : 'warning'} className="inline-block max-w-full truncate">{target.label}</Pill>)
                  : <span className="text-sm font-normal text-muted">対象アカウントなし</span>}
            </div>
          </div>
          <label className={labelClass}>シナリオ名<Input maxLength={100} value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} /></label>
          <label className={`${labelClass} md:col-span-2`}>説明<Textarea maxLength={500} value={editor.description} onChange={(e) => setEditor({ ...editor, description: e.target.value })} /></label>
          <label className={labelClass}>開始条件<select className={selectClass} value={editor.definition.trigger?.type ?? ''} onChange={(e) => { const type = e.target.value; setDefinition({ ...editor.definition, trigger: type === 'friend_add' ? { type } : type === 'tag_added' ? { type, tagId: null } : type === 'manual' ? { type } : null }); }}><option value="">未設定</option><option value="friend_add">友だち追加</option>{(canUseTags || editor.definition.trigger?.type === 'tag_added') && <option value="tag_added">タグ付与</option>}<option value="manual">手動登録</option></select>
            {/* 開始条件は「その瞬間」しか拾えないため、すでに条件を満たしている友だちの扱いを選択肢ごとに示す */}
            <span className="grid gap-1 text-xs font-normal text-muted">
              <span>この配信に友だちが入るきっかけです。ここで入った時点が各ステップの「登録」になります。</span>
              {triggerHint[editor.definition.trigger?.type ?? ''] && <span>{triggerHint[editor.definition.trigger?.type ?? '']}</span>}
            </span>
          </label>
          {editor.definition.trigger?.type === 'tag_added' && !canUseTags && <p className="text-xs text-muted">{tagsDisabledNote}</p>}
          {editor.definition.trigger?.type === 'tag_added' && canUseTags && <label className={labelClass}>開始タグ<select className={selectClass} value={editor.definition.trigger.tagId ?? ''} onChange={(e) => setDefinition({ ...editor.definition, trigger: { type: 'tag_added', tagId: Number(e.target.value) || null } })}><option value="">選択してください</option>{tags.map((tag) => <option key={tag.id} value={tag.id}>{tag.name}</option>)}</select></label>}
          {/* 入力欄ではなく操作なので格子から外す。開始条件の選択で「開始タグ」欄が増減してもボタン位置が動かない */}
          {editor.state === 'active' && <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border/70 pt-4 md:col-span-2"><Button type="button" size="sm" variant="outline" disabled={accountUnavailable} onClick={() => { setNotice(undefined); setManual({ type: 'all', tagId: '', key: uuidv4(), selected: [], page: 1, search: '' }); }}>友だちを手動登録</Button><span className="text-xs text-muted">すでに友だちになっている人へ、今から配信を始めます</span></div>}
        </div>
        <div className="grid gap-4">
          {editor.definition.steps.length > 0 && (
            <div className="flex items-center justify-between gap-2 text-xs text-muted">
              <span>ステップ {editor.definition.steps.length}件（見出しを押すと畳めます）</span>
              <Button type="button" size="sm" variant="outline" onClick={() => setClosedStepIds(closedStepIds.length ? [] : editor.definition.steps.map((step) => step.id))}>
                {closedStepIds.length ? 'すべて開く' : 'すべて閉じる'}
              </Button>
            </div>
          )}
          <ol className="grid gap-4">
            {editor.definition.steps.map((step, index) => <Fragment key={step.id}>
              <StepEditor step={step} index={index} steps={editor.definition.steps} tags={tags} canUseTags={canUseTags} assets={assetsQuery.data ?? []} references={detail?.assetReferences ?? []} onError={setNotice} testDisabled={accountUnavailable} open={!closedStepIds.includes(step.id)} onToggle={() => toggleStep(step.id)} onTest={() => { setNotice(undefined); setTest({ stepId: step.id, selected: [], page: 1, search: '' }); }} onRemove={() => requestRemoveStep(index)} onDuplicate={() => duplicateStep(index)} onMove={(offset) => { const steps = [...editor.definition.steps]; [steps[index], steps[index + offset]] = [steps[index + offset], steps[index]]; updateSteps(steps); }} onChange={(value) => updateSteps(editor.definition.steps.map((item, i) => i === index ? value : item))} onEditTiming={() => setTimingStepIndex(index)} onUploaded={applyUploadedMessage} working={working} onUploadingChange={changeUploading} />
              {/* 途中へ足すのに「末尾へ追加してから↑を連打」しなくて済むようにする */}
              {index < editor.definition.steps.length - 1 && editor.definition.steps.length < 100 && (
                <li className="relative sm:pl-14">
                  <button type="button" className="w-full rounded-2xl border border-dashed border-border/80 py-1.5 text-xs font-semibold text-muted transition hover:border-accent/60 hover:text-accent" onClick={() => insertStepAfter(index)}>＋ ここにステップを追加</button>
                </li>
              )}
            </Fragment>)}
          </ol>
          <Button type="button" variant="outline" disabled={editor.definition.steps.length >= 100} onClick={() => { const added = stepFor(); updateSteps([...editor.definition.steps, added]); setClosedStepIds((current) => current.filter((value) => value !== added.id)); }}>ステップを追加（最大100件）</Button>
        </div></fieldset>
      </section>

      <Modal open={Boolean(publish)} onClose={() => !working && setPublish(undefined)} size="lg">
        <Dialog.Title className="text-xl font-semibold">公開の確認</Dialog.Title>
        {publish && (
          <fieldset disabled={working} className="mt-4 grid gap-4 text-sm">
            {notice && <Alert>{notice}</Alert>}
            <p>新しく参加する友だちは、この公開内容を使います。</p>
            <label className="flex items-start gap-3 rounded-2xl border border-border/70 p-3">
              <input type="checkbox" className="mt-1" checked={publish.apply} disabled={!canApplyImpact(publish.impact)} onChange={(e) => setPublish({ ...publish, apply: e.target.checked })} />
              <span className="grid gap-1">
                <span className="font-semibold text-ink">現在進行中の配信にも、未配信分から反映する</span>
                <span className="text-xs leading-relaxed text-muted">{publish.apply ? '進行中の配信は、次に送るステップから新しい内容へ切り替わります。' : 'OFFのままだと、進行中の配信は今の内容のまま最後まで進みます。'}</span>
              </span>
            </label>
            {publish.impact && (
              <>
                <dl className="grid gap-2 sm:grid-cols-2">
                  {impactItems(publish.impact).map(({ testId, label, value, hint }) => (
                    <div key={label} data-testid={`publish-impact-${testId}`} className="grid content-start gap-1 rounded-2xl border border-border/70 bg-neutral-25 p-3">
                      <dt className="text-[11px] text-muted">{label}</dt>
                      <dd className="grid gap-1">
                        <span className="text-lg font-semibold text-ink">{value}</span>
                        <span className="text-[11px] leading-relaxed text-muted">{hint}</span>
                      </dd>
                    </div>
                  ))}
                </dl>
                {!canApplyImpact(publish.impact) && <Alert>{applyBlockReason(publish.impact)}</Alert>}
              </>
            )}
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setPublish(undefined)}>キャンセル</Button><Button type="button" loading={busy === 'publish'} disabled={publish.apply && !publish.impact} onClick={() => void commitPublish()}>この内容で公開</Button></div>
          </fieldset>
        )}
      </Modal>

      <Modal open={Boolean(manual)} onClose={() => !working && setManual(undefined)} size="lg">
        <Dialog.Title className="text-xl font-semibold">友だちを手動登録</Dialog.Title>
        {/* 配信されるのは公開済みの版で、編集中の下書きではない。テスト送信は下書きを送るため、
            両方を続けて使うと「テストで見た内容と違うものが届く」ことになる。版と未公開の有無を必ず出す */}
        {manual && !manual.result && (
          <div className="mt-4 grid gap-1 rounded-2xl bg-neutral-25 p-3 text-xs">
            <p className="font-semibold text-ink">{publishedVersionLabel}の内容で配信します</p>
            {editor.hasUnpublishedChanges && <p className="text-amber-700">未公開の変更は含まれません。反映するには先に「公開する」を実行してください。</p>}
          </div>
        )}
        {manual && <div className="mt-4 grid gap-4 text-sm">{notice && <Alert>{notice}</Alert>}{manual.result ? <><p>対象 {manual.result.targetCount}件のうち {manual.result.startedCount}件を開始しました（スキップ {manual.result.skippedCount}件）。</p>{manual.result.outcomes?.length ? <ul className="max-h-64 overflow-y-auto rounded-xl bg-neutral-25 p-3">{manual.result.outcomes.map((outcome) => <li key={outcome.friendId}>#{outcome.friendId}: {outcome.status === 'started' ? '開始' : `スキップ（${manualSkipReasonLabel[outcome.reason]}）`}</li>)}</ul> : null}<Button type="button" onClick={() => setManual(undefined)}>閉じる</Button></> : <fieldset disabled={working} className="grid gap-4"><label className={labelClass}>対象<select className={selectClass} value={manual.type} onChange={(event) => setManual({ type: event.target.value as ManualState['type'], tagId: '', key: uuidv4(), selected: [], page: 1, search: '' })}><option value="all">{manualAllLabel}</option>{canUseTags && <option value="tag">タグで指定</option>}<option value="friends">友だちを個別に指定</option></select></label>{manual.type === 'tag' && <label className={labelClass}>タグ<select className={selectClass} value={manual.tagId} onChange={(event) => setManual({ ...manual, tagId: event.target.value, key: uuidv4(), preview: undefined })}><option value="">選択してください</option>{tags.map((tag) => <option key={tag.id} value={tag.id}>{tag.name}</option>)}</select></label>}{manual.type === 'friends' ? <FriendPicker label="友だち" data={manualFriendsQuery.data} loading={manualFriendsQuery.isPending} error={manualFriendsQuery.error} selected={manual.selected} max={MANUAL_FRIEND_IDS_LIMIT} page={manual.page} search={manual.search} onSearch={(search) => setManual({ ...manual, search, page: 1 })} onPage={(page) => setManual({ ...manual, page })} onChange={(selected) => setManual({ ...manual, selected, key: uuidv4() })} /> : manual.preview ? <p className="rounded-xl bg-neutral-25 p-3">対象 {manual.preview.targetCount}件 / 開始予定 {manual.preview.startableCount}件 / スキップ予定 {manual.preview.targetCount - manual.preview.startableCount}件</p> : <p className="text-muted">件数を確認してから一括登録します（上限{number(MANUAL_TARGET_LIMIT)}人）。</p>}<div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setManual(undefined)}>キャンセル</Button>{manual.type === 'friends' ? <Button type="button" loading={busy === 'manual-commit'} disabled={accountUnavailable || !manual.selected.length} onClick={() => void commitManual()}>選択した{manual.selected.length}人を登録</Button> : manual.preview ? <Button type="button" loading={busy === 'manual-commit'} disabled={accountUnavailable} onClick={() => void commitManual()}>この内容で登録</Button> : <Button type="button" loading={busy === 'manual-preview'} disabled={accountUnavailable || (manual.type === 'tag' && !manual.tagId)} onClick={() => void previewManual()}>件数を確認</Button>}</div></fieldset>}</div>}
      </Modal>

      <Modal open={Boolean(test)} onClose={() => !working && setTest(undefined)} size="lg">
        <Dialog.Title className="text-xl font-semibold">このステップをテスト送信</Dialog.Title>
        {test && <div className="mt-4 grid gap-4 text-sm">{notice && <Alert>{notice}</Alert>}{test.result ? <><p>成功 {test.result.sentCount}件 / 失敗 {test.result.failedCount}件 / 結果不明 {test.result.unknownCount}件 / 未送信 {test.result.notSentCount}件</p><ul className="grid gap-1 rounded-xl bg-neutral-25 p-3">{test.result.outcomes.map((outcome) => <li key={outcome.friendId}>#{outcome.friendId}: {testStatusLabel[outcome.status]}{testOutcomeReason(outcome) ? `（${testOutcomeReason(outcome)}）` : ''}</li>)}</ul>{test.result.unknownCount > 0 && <Alert>届いている可能性があります。テスト端末で受信を確認してから再実行してください。再実行すると重複配信になる可能性があります。</Alert>}<Button type="button" onClick={() => setTest(undefined)}>閉じる</Button></> : test.prepared ? <fieldset disabled={working} className="grid gap-4"><p>選択した {test.prepared.recipients.length}人へ実配信と同じ内容を送ります。LINEの配信通数に算入されます。</p><p className="text-xs text-muted">タグ付与・配信の進行・集計には反映されません。</p><div className="grid gap-3"><div><p className="text-xs font-semibold text-muted">送信先</p>{testRecipients.length > 1 ? <><div className="mt-1 flex flex-wrap gap-2">{testRecipients.map(({ friendId }) => <button key={friendId} type="button" aria-pressed={friendId === testPreviewFriendId} onClick={() => setTest({ ...test, previewFriendId: friendId })} className={`rounded-[var(--btn-radius)] border px-3 py-1 text-xs font-semibold transition ${friendId === testPreviewFriendId ? 'border-accent bg-accent-soft text-ink' : 'border-border bg-white text-muted hover:border-accent/60'}`}>{test.names?.[friendId] ?? `#${friendId}`}</button>)}</div><p className="mt-1 text-xs text-muted">選んだ宛先に届く内容を表示しています。{testRecipients.length}人全員へ送ります。</p></> : <p className="mt-1 text-sm">{testRecipients.map(({ friendId }) => test.names?.[friendId] ?? `#${friendId}`).join('、')}</p>}</div>{testStep && <MessagePreview step={testStepIndex + 1} messages={testPreviewMessages} assets={assetsQuery.data ?? []} references={detail?.assetReferences ?? []} />}</div><p className="text-xs text-muted">確認期限: {formatJst(test.prepared.expiresAt)}</p><div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setTest({ ...test, prepared: undefined, previewFriendId: undefined })}>選び直す</Button><Button type="button" loading={busy === 'test-commit'} disabled={accountUnavailable} onClick={() => void commitTest()}>送信する</Button></div></fieldset> : <fieldset disabled={working} className="grid gap-4"><p>{testPickerNotice}</p><p className="rounded-xl bg-neutral-25 p-3 text-xs text-muted">内容の確認だけを行います。送るのは<span className="font-semibold text-ink">編集中の内容</span>で、実際の配信に使われる公開済みの内容とは異なる場合があります。「送信成功時に付けるタグ」の付与、送信条件の判定、配信の進行、集計への反映は行いません。タグ付与まで確かめる場合は、テストユーザーを手動登録して実際の配信を1本流してください。</p><FriendPicker label="テストユーザー" data={testFriendsQuery.data} loading={testFriendsQuery.isPending} error={testFriendsQuery.error} selected={test.selected} max={10} page={test.page} search={test.search} onSearch={(search) => setTest({ ...test, search, page: 1 })} onPage={(page) => setTest({ ...test, page })} onChange={(selected) => setTest({ ...test, selected })} /><div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setTest(undefined)}>キャンセル</Button><Button type="button" loading={busy === 'test-prepare'} disabled={accountUnavailable || test.selected.length < 1} onClick={() => void prepareTest()}>内容を確認</Button></div></fieldset>}</div>}
      </Modal>
      <ConfirmModal
        open={confirmRemoveStep !== null}
        textTitle="このステップを削除しますか？"
        textMessage={confirmRemoveStep === null ? '' : `ステップ ${confirmRemoveStep + 1}（メッセージ${editor.definition.steps[confirmRemoveStep]?.messages.length ?? 0}件）を削除します。入力した内容は元に戻せません。`}
        textCancel="やめる"
        textConfirm="削除する"
        confirmButtonClassName="border-danger bg-danger text-white !shadow-none hover:border-danger hover:bg-danger hover:text-white"
        onClose={() => setConfirmRemoveStep(null)}
        onConfirm={() => confirmRemoveStep !== null && removeStep(confirmRemoveStep)}
      />
      <ConfirmModal
        open={confirmArchive}
        textTitle="シナリオをアーカイブしますか？"
        textMessage={`進行中の配信はすべて取り消され、一覧から隠れます。公開した内容と配信の記録は残りますが、取り消した配信は復元しても元に戻りません。${dirty ? '未保存の変更は破棄されます。' : ''}`}
        textCancel="やめる"
        textConfirm="アーカイブする"
        confirmButtonClassName="border-danger bg-danger text-white !shadow-none hover:border-danger hover:bg-danger hover:text-white"
        isLoading={busy === 'archive'}
        onClose={() => setConfirmArchive(false)}
        onConfirm={() => void runAction('archive')}
      />
      {timingStepIndex !== null && editor.definition.steps[timingStepIndex] && (
        <TimingModal
          open
          step={timingStepIndex + 1}
          value={editor.definition.steps[timingStepIndex].timing}
          onClose={() => setTimingStepIndex(null)}
          onDecide={(timing) => {
            updateSteps(editor.definition.steps.map((item, i) => (i === timingStepIndex ? { ...item, timing } : item)));
            setTimingStepIndex(null);
          }}
        />
      )}
      <Snackbar open={Boolean(success)} message={success} variant="success" onClose={() => setSuccess(undefined)} autoHideDuration={3000} />
    </>
  );
}
